import { AppUrls } from 'common/constants/AppUrl';
import { useIframeMessageListener } from 'contexts/IframeMessageListener';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from 'store';
import { setSelectedSite } from 'store/slices/site';
import { SelectSitePayload } from './site.types';
import { postIframeMessage } from 'utils/postPerentIframeMessage';
import { ensureLocationChangeEventInstalled } from 'utils/locationChangeEvent';
import { safeStringify } from 'utils/safeStringify';
import { SESSION_LOGGING_MESSAGE_KEY } from 'pages/SiteLoadingPage/constants';

export const useMasterSiteChange = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const onSiteSelect = useCallback(
    (data: SelectSitePayload) => {
      const target = AppUrls.SitePages.InitializationPage;
      postIframeMessage(SESSION_LOGGING_MESSAGE_KEY, {
        step: 'useMasterSiteChange:onSiteSelect',
        details: { ...data, pageToNavigateTo: target }
      });
      try {
        dispatch(setSelectedSite(data));
        ensureLocationChangeEventInstalled();

        const currentPath = window.location.pathname;
        const isAtTarget = currentPath === target || currentPath.endsWith(target);

        if (!isAtTarget) {
          const onLocationChange = () => {
            const updatedPath = window.location.pathname;
            const isNowAtTarget = updatedPath === target || updatedPath.endsWith(target);
            if (!isNowAtTarget) {
              postIframeMessage(SESSION_LOGGING_MESSAGE_KEY, {
                type: 'error',
                step: 'useMasterSiteChange:onSiteSelect:navigateFallback',
                details: { currentPath: updatedPath, target }
              });
              navigate(target, { replace: true, unstable_flushSync: true });
            }
          };

          window.addEventListener('locationchange', onLocationChange, { once: true });
        }

        navigate(target, { replace: true, unstable_flushSync: true });
        postIframeMessage(SESSION_LOGGING_MESSAGE_KEY, {
          step: 'useMasterSiteChange:onSiteSelect:navigateCalled',
          details: { pathname: window.location.pathname, target }
        });
      } catch (error) {
        postIframeMessage(SESSION_LOGGING_MESSAGE_KEY, {
          type: 'error',
          step: 'useMasterSiteChange:onSiteSelect:navigateFailed',
          details: {
            target,
            error: safeStringify(error)
          }
        });
        navigate(target, { replace: true, unstable_flushSync: true });
      }
    },
    [dispatch, navigate]
  );
  useIframeMessageListener('onSelectSite', onSiteSelect);
};
